import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';

import Form, { FormInput } from 'components/common/Form';
import { SetNewPasswordFormType } from 'store/auth';
import { setNewPasswordSchema } from 'configs/validationSchemes';
import useYupValidationResolver from 'hooks/useYupValidationResolver';
import Modal from 'components/common/Modal';
import DotIcon from 'components/common/icons/Dot';
import useScreen from 'hooks/useScreen';
import { TranslationKey } from 'utils/translate';
import useToaster from 'hooks/useToaster';
import { setNewPassword } from 'api/requests/account';

import sx from './styles';

export interface ISetNewPasswordModalProps {
  onClose: () => void;
  resetToken: string;
}

const SetNewPasswordModal: FC<ISetNewPasswordModalProps> = ({ onClose, resetToken }) => {
  const notify = useToaster();
  const { isMobile } = useScreen();
  const [loading, setLoading] = useState(false);

  const resolver = useYupValidationResolver<{ newPassword: string }>(setNewPasswordSchema);
  const { control, handleSubmit } = useForm<SetNewPasswordFormType>({
    resolver,
  });

  const handleSetNewSubmit = async (data: SetNewPasswordFormType): Promise<void> => {
    setLoading(true);
    try {
      await setNewPassword({
        newPassword: data.newPassword,
        passwordResetToken: resetToken,
      });
      onClose();
      notify({
        message: 'Success',
        type: 'success',
      });
    } catch (error) {
      notify({
        message: ((error as Error)?.message || 'Something went wrong') as TranslationKey,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (): Promise<void> => {
    handleSubmit(handleSetNewSubmit)();
  };

  return (
    <Modal
      title="Set New Password"
      icon={<DotIcon fill="red.scale.300" />}
      saveText="Apply"
      isLoading={loading}
      onSave={onSave}
      onClose={onClose}
      maxWidth={isMobile ? 600 : 400}
    >
      <Form sx={sx.form} spacing={2.5} onSubmit={handleSubmit(handleSetNewSubmit)}>
        <FormInput<SetNewPasswordFormType>
          required
          label="New Password"
          control={control}
          name="newPassword"
          type="password"
        />
        <FormInput<SetNewPasswordFormType>
          required
          label="Confirm new password"
          control={control}
          name="confirmPassword"
          type="password"
        />
      </Form>
    </Modal>
  );
};

export default SetNewPasswordModal;
